import React, { useEffect, useState } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import { Button, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../../_helper/LoaderContext";
import PhoneInput from "react-phone-input-2";
import { toast } from "react-toastify";
import {
	createPharmaDoctorAPI,
	getPharmaDoctorAPI,
	updatePharmaDoctorAPI,
} from "../PharmaDoctorService";

const EMPTY_FORM = {
	name: "",
	email: "",
	phoneNumber: "",
	phoneCode: "+91",
	registrationNumber: "",
	qualification: "",
	speciality: "",
	clinicName: "",
	clinicAddress: "",
	note: "",
};

const PharmaDoctorModal = ({ show, onHide, doctorId, mode = "add", onSaved }) => {
	const { t } = useTranslation();
	const { setLoading } = useLoader();
	const isView = mode === "view";
	const isEdit = mode === "edit";

	const [form, setForm] = useState(EMPTY_FORM);
	const [formErrors, setFormErrors] = useState({});

	useEffect(() => {
		if (!show) {
			setForm(EMPTY_FORM);
			setFormErrors({});
			return;
		}

		if ((isEdit || isView) && doctorId) {
			loadDoctor(doctorId);
		} else {
			setForm(EMPTY_FORM);
			setFormErrors({});
		}
	}, [show, doctorId, mode]);

	const loadDoctor = async (id) => {
		try {
			setLoading(true);
			const response = await getPharmaDoctorAPI({ doctorId: id });
			const doctor = response?.data?.data || response?.data?.doctor || {};
			setForm({
				name: doctor.name || "",
				email: doctor.email || "",
				phoneNumber: doctor.phoneNumber || "",
				phoneCode: doctor.phoneCode || "+91",
				registrationNumber: doctor.registrationNumber || "",
				qualification: doctor.qualification || "",
				speciality: doctor.speciality || doctor.specialty || "",
				clinicName: doctor.clinicName || "",
				clinicAddress: doctor.clinicAddress || "",
				note: doctor.note || "",
			});
		} catch (error) {
			console.error("Error loading doctor:", error);
			toast.error(t("Failed to load doctor details"));
			onHide();
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		if (formErrors[field]) {
			setFormErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const handlePhoneChange = (value, country) => {
		const dialCode = country?.dialCode ? `+${country.dialCode}` : form.phoneCode;
		const phoneNumber = value.slice(country?.dialCode?.length || 0);
		setForm((prev) => ({
			...prev,
			phoneCode: dialCode,
			phoneNumber,
		}));
	};

	const getPhoneValue = () => {
		const code = (form.phoneCode || "").replace(/\D/g, "");
		const number = (form.phoneNumber || "").replace(/\D/g, "");
		return code && number ? `${code}${number}` : number;
	};

	const validate = () => {
		const errors = {};
		if (!form.name?.trim()) {
			errors.name = t("Doctor name is required");
		}
		if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
			errors.email = t("Enter a valid email address");
		}
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const buildPayload = () => ({
		name: form.name.trim(),
		email: form.email?.trim() || "",
		phoneNumber: form.phoneNumber?.trim() || "",
		phoneCode: form.phoneCode || "",
		registrationNumber: form.registrationNumber?.trim() || "",
		qualification: form.qualification?.trim() || "",
		speciality: form.speciality?.trim() || "",
		clinicName: form.clinicName?.trim() || "",
		clinicAddress: form.clinicAddress?.trim() || "",
		note: form.note?.trim() || "",
	});

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!validate()) return;

		try {
			setLoading(true);
			const payload = buildPayload();

			if (isEdit) {
				const response = await updatePharmaDoctorAPI({
					doctorId,
					...payload,
				});
				toast.success(response?.data?.message || t("Doctor updated successfully"));
			} else {
				const response = await createPharmaDoctorAPI(payload);
				toast.success(response?.data?.message || t("Doctor added successfully"));
			}

			onSaved?.();
			onHide();
		} catch (error) {
			console.error("Error saving doctor:", error);
		} finally {
			setLoading(false);
		}
	};

	const title = isView
		? t("Doctor Details")
		: isEdit
			? t("Edit Doctor Details")
			: t("Add A Doctor");

	return (
		<Modal
			show={show}
			onHide={onHide}
			size="lg"
			centered
			className="pharma-doctor-modal"
			backdrop="static"
		>
			<Modal.Header closeButton>
				<Modal.Title>{title}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<form onSubmit={handleSubmit} id="pharma-doctor-form">
					<Row className="g-3">
						<Col md={6}>
							<label className="form-label">
								{t("Doctor Name")} <span className="text-danger">*</span>
							</label>
							<Input
								type="text"
								value={form.name}
								disabled={isView}
								placeholder={t("e.g. Dr. Rahul")}
								onChange={(e) => handleChange("name", e.target.value)}
								invalid={Boolean(formErrors.name)}
							/>
							{formErrors.name && (
								<div className="text-danger small mt-1">{formErrors.name}</div>
							)}
						</Col>

						<Col md={6}>
							<label className="form-label">{t("Registration / Licence No.")}</label>
							<Input
								type="text"
								value={form.registrationNumber}
								disabled={isView}
								placeholder={t("e.g. MCI-12345")}
								onChange={(e) => handleChange("registrationNumber", e.target.value)}
							/>
						</Col>

						<Col md={6}>
							<label className="form-label">{t("Email")}</label>
							<Input
								type="email"
								value={form.email}
								disabled={isView}
								placeholder={t("email@example.com")}
								onChange={(e) => handleChange("email", e.target.value)}
								invalid={Boolean(formErrors.email)}
							/>
							{formErrors.email && (
								<div className="text-danger small mt-1">{formErrors.email}</div>
							)}
						</Col>

						<Col md={6}>
							<label className="form-label">{t("Phone Number")}</label>
							<PhoneInput
								country="in"
								value={getPhoneValue()}
								disabled={isView}
								onChange={handlePhoneChange}
								inputClass="form-control"
								containerClass="w-100"
								enableSearch
							/>
						</Col>

						<Col md={6}>
							<label className="form-label">{t("Qualification")}</label>
							<Input
								type="text"
								value={form.qualification}
								disabled={isView}
								placeholder={t("e.g. MBBS, MD")}
								onChange={(e) => handleChange("qualification", e.target.value)}
							/>
						</Col>

						<Col md={6}>
							<label className="form-label">{t("Speciality")}</label>
							<Input
								type="text"
								value={form.speciality}
								disabled={isView}
								placeholder={t("e.g. Cardiology")}
								onChange={(e) => handleChange("speciality", e.target.value)}
							/>
						</Col>

						<Col md={6}>
							<label className="form-label">{t("Clinic / Hospital Name")}</label>
							<Input
								type="text"
								value={form.clinicName}
								disabled={isView}
								placeholder={t("Clinic name")}
								onChange={(e) => handleChange("clinicName", e.target.value)}
							/>
						</Col>

						<Col md={6}>
							<label className="form-label">{t("Clinic Address")}</label>
							<Input
								type="text"
								value={form.clinicAddress}
								disabled={isView}
								placeholder={t("Clinic address")}
								onChange={(e) => handleChange("clinicAddress", e.target.value)}
							/>
						</Col>

						<Col md={12}>
							<label className="form-label">{t("Note")}</label>
							<Input
								type="textarea"
								rows={3}
								value={form.note}
								disabled={isView}
								placeholder={t("Additional notes")}
								onChange={(e) => handleChange("note", e.target.value)}
							/>
						</Col>
					</Row>
				</form>
			</Modal.Body>
			<Modal.Footer>
				<Button color="secondary" onClick={onHide}>
					{isView ? t("Close") : t("Cancel")}
				</Button>
				{!isView && (
					<Button color="primary" type="submit" form="pharma-doctor-form">
						{isEdit ? t("Update") : t("Save")}
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default PharmaDoctorModal;
